// src/components/Map.js again
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NoSleep from 'nosleep.js';
import SpotListModal from '../../components/SpotListModal';
import StampListModal from '../../components/StampListModal';
import QuizModal from '../../components/QuizModal';
import InfoModal from '../../components/InfoModal';
import VideoModal from '../../components/VideoModal';
import StampModal from '../../components/StampModal';
import InputQuizModal from '../../components/InputQuizModal';
import QuizResultModal from '../../components/QuizResultModal';
import ProximityModal from '../../components/ProximityModal';
import AudioPermissionModal from '../../components/AudioPermissionModal';
import CongratulationsModal from '../../components/CongratulationsModal';
import audioManager from '../../utils/AudioManager';
import '../../styles/Map.css';
import { startGuideSession, endGuideSession, saveLocation } from '../../utils/firebase';
import { LOCATION_UPLOAD_INTERVAL } from '../../config/firebase';
import spots from '../../settings/HattoriRyokuchi/spot.json';

// Leafletのデフォルトアイコン修正
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const defaultCenter = {
  lat: 34.677560,
  lng: 135.480345,
};

// UI/UX改善: フルスクリーン対応
const mapContainerStyle = {
  width: '100%',
  height: '100vh',
  minHeight: 'none',
  maxHeight: 'none'
};

// カテゴリ別カスタムアイコンの作成
function customPinIcon(category, isCompleted) {
  const iconMap = {
    'Guide': '/images/map_pin/guide-icon.svg',
    'Video': '/images/map_pin/guide-icon.svg',
    'Warning': '/images/map_pin/note-icon.svg',
    'Stamp': '/images/map_pin/stampspot-icon.svg',
    'Select_Quiz': '/images/map_pin/quiz-icon.svg',
    'Input_Quiz': '/images/map_pin/quiz-icon.svg',
    'Charge_Station': '/images/map_pin/battery-icon.svg',
  };

  // 完了済みの時に上書き
  if (isCompleted) {
    if (category === 'Stamp') {
      return L.icon({
        iconUrl: '/images/map_pin/stampget-icon.svg',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40]
      });
    }
    if (category === 'Select_Quiz' || category === 'Input_Quiz') {
      return L.icon({
        iconUrl: '/images/map_pin/quizclear-icon.svg',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40]
      });
    }
  }

  const iconUrl = iconMap[category] || '/images/map_pin/guide-icon.svg';

  return L.icon({
    iconUrl,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
}

const currentLocationIcon = L.icon({
  iconUrl: '/images/position-icon.svg',
  iconSize: [30, 30],
  iconAnchor: [15, 15]
});

// 移動可能エリアの定義
const movableArea = [
  [34.775542, 135.486618],
  [34.774875, 135.486256],
  [34.77472, 135.486035],
  [34.774318, 135.485847],
  [34.77397, 135.485867],
  [34.773656, 135.485706],
  [34.773342, 135.48539],
  [34.77289, 135.484585],
  [34.772851, 135.484229],
  [34.772895, 135.48392],
  [34.77316, 135.483571],
  [34.773855, 135.483336],
  [34.77413, 135.483403],
  [34.7745, 135.482799],
  [34.774952, 135.482678],
  [34.775018, 135.482873],
  [34.775476, 135.48347],
  [34.776126, 135.48343],
  [34.776027, 135.482369],
  [34.77627, 135.482309],
  [34.776391, 135.483041],
  [34.776935, 135.483247],
  [34.778167, 135.483712],
  [34.778564, 135.483558],
  [34.778685, 135.483833],
  [34.778304, 135.484054],
  [34.778183, 135.48441],
  [34.778128, 135.484907],
  [34.778194, 135.485283],
  [34.778105, 135.485529],
  [34.778093, 135.485557],
  [34.778062, 135.485639],
  [34.778861, 135.486122],
  [34.779209, 135.486303],
  [34.77949, 135.486612],
  [34.779722, 135.48731],
  [34.779837, 135.487935],
  [34.779887, 135.488613],
  [34.779672, 135.488619],
  [34.779666, 135.488082],
  [34.779562, 135.487438],
  [34.77938, 135.486921],
  [34.779076, 135.486626],
  [34.778018, 135.485934],
  [34.77675, 135.48492],
  [34.776876, 135.487102],
  [34.777351, 135.487371],
  [34.77756, 135.487659],
  [34.77767, 135.487968],
  [34.777648, 135.488498],
  [34.777532, 135.48876],
  [34.777334, 135.489036],
  [34.77713, 135.489203],
  [34.776766, 135.489244],
  [34.776545, 135.48921],
  [34.776292, 135.488982],
  [34.776066, 135.488586],
  [34.775978, 135.488297],
  [34.775779, 135.488317],
  [34.775773, 135.489425],
  [34.775603, 135.489432],
  [34.775603, 135.488344],
  [34.774698, 135.488351],
  [34.774698, 135.488062],
  [34.775183, 135.48807],
  [34.775184, 135.487837],
  [34.775307, 135.487371],
  [34.775611, 135.487221],
  [34.775542, 135.486618]
];

const movableArea2 = [
  [34.776286, 135.483766],
  [34.77548, 135.483862],
  [34.774765, 135.482992],
  [34.774316, 135.483781],
  [34.773898, 135.483626],
  [34.774668, 135.484748],
  [34.774777, 135.485109],
  [34.77485, 135.485626],
  [34.77488, 135.48584],
  [34.775032, 135.486054],
  [34.776044, 135.486718],
  [34.776607, 135.486659],
  [34.77628, 135.483774]
]

const movableArea3 = [
  [34.776873, 135.483458],
  [34.776451, 135.483234],
  [34.77652, 135.48371],
  [34.777019, 135.483626],
  [34.776888, 135.483474]
]

const movableArea4 = [
  [34.777943, 135.485556],
  [34.776997, 135.484595],
  [34.776709, 135.484584],
  [34.77661, 135.483926],
  [34.777195, 135.483707],
  [34.778033, 135.483926],
  [34.77813, 135.483951],
  [34.778091, 135.484175],
  [34.778017, 135.484435],
  [34.778011, 135.484721],
  [34.778098, 135.485238],
  [34.777943, 135.485556]
]

const movableArea5 = [
  [34.775751, 135.487303],
  [34.775751, 135.487304],
  [34.775739, 135.487537],
  [34.775739, 135.488142],
  [34.775826, 135.488159],
  [34.775951, 135.488155],
  [34.776071, 135.487767],
  [34.776248, 135.487458],
  [34.776612, 135.487216],
  [34.776617, 135.487001],
  [34.776066, 135.487001],
  [34.775876, 135.487078],
  [34.775753, 135.487304],
  [34.775751, 135.487303]
]

const movableAreas = [
  movableArea,
  movableArea2,
  movableArea3,
  movableArea4,
  movableArea5
];

// ポリゴンスタイル
const polygonStyle = {
  fillColor: '#4285f4',
  fillOpacity: 0.1,
  color: '#4285f4',
  opacity: 0.8,
  weight: 2
};

// ルート線スタイル
const routeStyle = {
  color: '#ff4d4f',
  weight: 5,
  opacity: 0.8,
};

const OUTSIDE_AREA_ALERT_INTERVAL = 5;

// ピンロケーション（IDを追加）
const pinLocations = spots.map((spot, index) => ({
  ...spot,
  id: spot.id || `spot-${index}` // IDがない場合は生成
}));

// ★ スタンプ対象カテゴリ（スタンプ＋クイズも対象化）
const STAMPABLE_CATEGORIES = new Set(['Stamp', 'Select_Quiz', 'Input_Quiz']);

// カテゴリとモーダルタイプのマッピング
const categoryModalMap = {
  'Guide': 'info',
  'Video': 'video',
  'Warning': 'info',
  'Stamp': 'stamp',
  'Select_Quiz': 'quiz',
  'Input_Quiz': 'inputQuiz',
  'Charge_Station': 'info'
};

// マップ中心移動とズーム用のコンポーネント
function MapController({ center, shouldPanTo, targetZoom, resetZoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (shouldPanTo && center) {
      if (targetZoom && targetZoom !== map.getZoom()) {
        map.setView([center.lat, center.lng], targetZoom);
      } else {
        map.panTo([center.lat, center.lng]);
      }
    }
  }, [map, center, shouldPanTo, targetZoom]);
  
  useEffect(() => { if (resetZoom) { /* noop */ } }, [resetZoom]);
  
  return null;
}

// 現在地マーカーコンポーネント
function CurrentLocationMarker({ position, onClick, t }) {
  if (!position) return null;
  
  return (
    <Marker 
      position={[position.lat, position.lng]} 
      icon={currentLocationIcon}
      eventHandlers={{
        click: onClick
      }}
    >
      <Popup>{t('map.currentLocation')}</Popup>
    </Marker>
  );
}

function Map({ center = defaultCenter, zoom = 18 }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const LOCALE = i18n.language || 'ja';

  const [showSpotList, setShowSpotList] = useState(false);
  const [showStampList, setShowStampList] = useState(false);
  const [activeModal, setActiveModal] = useState({ type: null, data: null });
  const [quizResult, setQuizResult] = useState({ show: false, isCorrect: false });
  const [currentPosition, setCurrentPosition] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isFollowingLocation, setIsFollowingLocation] = useState(true);
  const [mapCenter, setMapCenter] = useState(center);
  const [proximityModal, setProximityModal] = useState({ show: false, spotInfo: null });
  const [dismissedSpots, setDismissedSpots] = useState(new Set());
  const [lastNearSpot, setLastNearSpot] = useState(null);
  const [showArea] = useState(true);
  const [isOutsideArea, setIsOutsideArea] = useState(false);
  const [outsideAreaTimer, setOutsideAreaTimer] = useState(null);
  const [targetZoom, setTargetZoom] = useState(null);
  const [isSpotMoving, setIsSpotMoving] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const sessionStartedRef = useRef(false);
  const [lastPlayedSpot, setLastPlayedSpot] = useState(null);
  const [showAudioPermission, setShowAudioPermission] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [completedStampIds, setCompletedStampIds] = useState(new Set());
  const [stamps, setStamps] = useState([]);
  const [routePath, setRoutePath] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  
  const watchIdRef = useRef(null);
  const locationTimerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const noSleepRef = useRef(null);

  // 多言語対応のテキスト取得関数
  const getLocalizedText = useCallback((textObj, fallbackLocale = 'ja') => {
    if (typeof textObj === 'string') return textObj;
    if (!textObj) return '';
    return textObj[LOCALE] || textObj[fallbackLocale] || textObj.ja || textObj.en || textObj.zh || textObj.ko || '';
  }, [LOCALE]);

  // 音声パス取得関数（多言語対応）
  const getAudioPath = useCallback((spot) => {
    if (!spot) return null;
    if (spot.voice && spot.voice[LOCALE]) {
      return spot.voice[LOCALE];
    }
    if (spot.voice && spot.voice.ja) {
      return spot.voice.ja;
    }
    return null;
  }, [LOCALE]);

  // 動画データ取得関数（多言語対応）
  const getVideoData = useCallback((spot) => {
    if (!spot?.video) return null;

    if (spot.video[LOCALE]) {
      return spot.video[LOCALE];
    }

    if (spot.video.ja) {
      return spot.video.ja;
    }

    if (spot.video.url) {
      return spot.video;
    }

    return null;
  }, [LOCALE]);

  // 画面消灯防止
  const enableScreenAwake = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        if (!wakeLockRef.current) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');

          wakeLockRef.current.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
        }
        return;
      }

      if (!noSleepRef.current) {
        noSleepRef.current = new NoSleep();
      }
      noSleepRef.current.enable();
    } catch (err) {
      console.error('Failed to enable screen awake:', err);

      try {
        if (!noSleepRef.current) {
          noSleepRef.current = new NoSleep();
        }
        noSleepRef.current.enable();
      } catch (fallbackErr) {
        console.error('NoSleep fallback failed:', fallbackErr);
      }
    }
  }, []);

  const disableScreenAwake = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (err) {
      console.error('Failed to release wake lock:', err);
    }

    try {
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
    } catch (err) {
      console.error('Failed to disable NoSleep:', err);
    }
  }, []);

  // スポットデータの生成（多言語対応）
  const spotData = spots.map((spot, i) => ({
    id: i + 1,
    name: getLocalizedText(spot.title),
    description: getLocalizedText(spot.description),
    image: spot.thumbnail,
    category: spot.category,
  }));

  // 初期化時にスタンプデータを設定
  useEffect(() => {
    const savedStamps = localStorage.getItem('completedStamps');
    if (savedStamps) {
      const savedIds = new Set(JSON.parse(savedStamps));
      setCompletedStampIds(savedIds);
      
      // クリア判定はスタンプ対象カテゴリで
      const total = pinLocations.filter(p => STAMPABLE_CATEGORIES.has(p.category)).length;
      if (savedIds.size === total && total > 0) {
        setShowCongratulations(true);
      }
    }
  
    // スタンプ一覧（スタンプ対象カテゴリのみ）
    const initialStamps = pinLocations
      .filter(pin => STAMPABLE_CATEGORIES.has(pin.category))
      .map(pin => ({
        id: pin.id,
        name: getLocalizedText(pin.title),
        completed: false,
        category: pin.category,
      }));
    setStamps(initialStamps);
  }, [getLocalizedText]);

  // スタンプ取得処理（クイズ正解/スタンプ取得に共通）
  const handleStampGet = useCallback((spotId) => {
    if (completedStampIds.has(spotId)) {
      return;
    }
  
    const newCompletedIds = new Set(completedStampIds);
    newCompletedIds.add(spotId);
    setCompletedStampIds(newCompletedIds);
  
    setStamps(prevStamps => 
      prevStamps.map(stamp => 
        stamp.id === spotId 
          ? { ...stamp, completed: true }
          : stamp
      )
    );
  
    const newCount = newCompletedIds.size;
  
    const totalStamps = pinLocations.filter(p => STAMPABLE_CATEGORIES.has(p.category)).length;
    if (newCount === totalStamps && totalStamps > 0) {
      setTimeout(() => {
        setShowCongratulations(true);
      }, 1000);
    }
  
    localStorage.setItem('completedStamps', JSON.stringify([...newCompletedIds]));
  }, [completedStampIds]);

  const closeCongratulations = () => {
    setShowCongratulations(false);
  };

  const stampsWithStatus = stamps.map(stamp => ({
    ...stamp,
    completed: completedStampIds.has(stamp.id)
  }));

  // 音声許可処理
  const handleAudioAllow = useCallback(async () => {
    try {
      if (!window.audioContextForApp) {
        window.audioContextForApp = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (window.audioContextForApp.state === 'suspended') {
        await window.audioContextForApp.resume();
      }
      const osc = window.audioContextForApp.createOscillator();
      const g = window.audioContextForApp.createGain();
      osc.connect(g);
      g.connect(window.audioContextForApp.destination);
      g.gain.value = 0;
      osc.start();
      osc.stop(window.audioContextForApp.currentTime + 0.05);
      audioManager.setExternalContext(window.audioContextForApp);
    } catch (e) {
      console.error('AudioContext setup failed:', e);
    }
    setAudioEnabled(true);
    setShowAudioPermission(false);

    enableScreenAwake();
  }, [enableScreenAwake]);

  const handleAudioDeny = useCallback(() => {
    setAudioEnabled(false);
    setShowAudioPermission(false);

    enableScreenAwake();
  }, [enableScreenAwake]);

  // LeafletのCSS読み込み
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // 画面復帰時に再取得
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !showAudioPermission) {
        enableScreenAwake();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enableScreenAwake, showAudioPermission]);

  // ポリゴン内判定関数
  const isPointInPolygon = useCallback((point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (((polygon[i][0] > point.lat) !== (polygon[j][0] > point.lat)) &&
          (point.lng < (polygon[j][1] - polygon[i][1]) * (point.lat - polygon[i][0]) / 
           (polygon[j][0] - polygon[i][0]) + polygon[i][1])) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  const outsideAreaAlertTimerRef = useRef(null);

  // エリア境界チェック
  const checkAreaBoundary = useCallback(() => {
    if (!currentPosition || !showArea) return;

    const isInside = isPointInPolygon(currentPosition, movableArea);
    const isInside2 = isPointInPolygon(currentPosition, movableArea2);
    const isInside3 = isPointInPolygon(currentPosition, movableArea3);
    const isInside4 = isPointInPolygon(currentPosition, movableArea4);
    const isInside5 = isPointInPolygon(currentPosition, movableArea5);

    if (!isInside || isInside2 || isInside3 || isInside4 || isInside5) {
      if (!isOutsideArea) {
        console.log('outside area detected', {
          audioEnabled,
          alertPath: '/audio/alert.mp3'
        });

        setIsOutsideArea(true);
        
        const timer = setTimeout(() => {
          alert(t('map.alerts.outsideArea'));
          setOutsideAreaTimer(null);
        }, 10000000000);

        setOutsideAreaTimer(timer);
      }

    } else {
      if (isOutsideArea) {
        setIsOutsideArea(false);
        
        if (outsideAreaTimer) {
          clearTimeout(outsideAreaTimer);
          setOutsideAreaTimer(null);
        }
      }
    }
  }, [
    currentPosition,
    showArea,
    isOutsideArea,
    outsideAreaTimer,
    isPointInPolygon,
    t,
    audioEnabled
  ]);

  // 距離計算関数
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  const isWithinProximity = useCallback((spot) => {
    if (!currentPosition || !spot) return false;
    const d = calculateDistance(
      currentPosition.lat,
      currentPosition.lng,
      spot.latitude,
      spot.longitude
    );
    // spot.radius があれば優先。なければ 100m を既定に
    const threshold = spot.radius || 100;
    return d <= threshold;
  }, [currentPosition, calculateDistance]);

  // OSRMで現在地から指定スポットまでの経路を取得
  const fetchRoute = useCallback(async (start, end) => {
    const url = `https://router.project-osrm.org/route/v1/foot/${start.lng},${start.lat};${end.lng},${end.lat}?overview=full&geometries=geojson`;

    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok || data.code !== 'Ok' || !data.routes?.length) {
      throw new Error(data.message || '経路が取得できませんでした');
    }

    return data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
  }, []);

  // 選択したスポットまでの経路検索
  const handleRouteSearch = useCallback(async (pin) => {
    if (!currentPosition) {
      alert(t('map.alerts.gettingLocation', '現在地を取得中です'));
      return;
    }

    try {
      setRouteLoading(true);

      const end = {
        lat: pin.latitude,
        lng: pin.longitude
      };

      const route = await fetchRoute(currentPosition, end);
      setRoutePath(route);
    } catch (error) {
      console.error('Failed to fetch route:', error);
      alert('経路を取得できませんでした');
    } finally {
      setRouteLoading(false);
    }
  }, [currentPosition, fetchRoute, t]);

  // 近接チェック
  const checkProximity = useCallback(async () => {
    if (!currentPosition) return;
      // エリア外チェック
      const isInside = isPointInPolygon(currentPosition, movableArea);
      const isInside2 = isPointInPolygon(currentPosition, movableArea2);
      const isInside3 = isPointInPolygon(currentPosition, movableArea3);
      const isInside4 = isPointInPolygon(currentPosition, movableArea4);
      const isInside5 = isPointInPolygon(currentPosition, movableArea5);
      if (!isInside || isInside2 || isInside3 || isInside4 || isInside5) {
        if (audioEnabled && !outsideAreaAlertTimerRef.current) {
          try {
            await window.audioContextForApp?.resume();
          } catch {}
          audioManager.playSimple('/audio/Alert.mp3');
        
          outsideAreaAlertTimerRef.current = setInterval(async () => {
            try {
              await window.audioContextForApp?.resume();
            } catch {}
            audioManager.playSimple('/audio/Alert.mp3');
          }, OUTSIDE_AREA_ALERT_INTERVAL * 1000);
        }
      } else {
        if (outsideAreaAlertTimerRef.current) {
          clearInterval(outsideAreaAlertTimerRef.current);
          outsideAreaAlertTimerRef.current = null;
        }
      }

    const PROXIMITY_THRESHOLD = 100;

    let closestPin = null;
    let closestDistance = Infinity;

    pinLocations.forEach(pin => {
      const distance = calculateDistance(
        currentPosition.lat,
        currentPosition.lng,
        pin.latitude,
        pin.longitude
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPin = pin;
      }
    });

    if (closestPin && closestDistance <= PROXIMITY_THRESHOLD) {
      if (!dismissedSpots.has(closestPin.id)) {
        const currentModalSpotId = proximityModal.spotInfo?.id;
        const isNewSpot = currentModalSpotId !== closestPin.id;
        
        if (isNewSpot || !proximityModal.show) {
          setProximityModal({
            show: true,
            spotInfo: {
              id: closestPin.id,
              title: getLocalizedText(closestPin.title),
              description: getLocalizedText(closestPin.summary || closestPin.description),
              image: closestPin.thumbnail,
              icon: closestPin.category === 'Guide' ? '📍' : 
                    closestPin.category === 'Warning' ? '⚠️' :
                    closestPin.category === 'Stamp' ? '🏆' : '❓'
            }
          });
          
          if (lastPlayedSpot !== closestPin.id) {
            const audioPath = getAudioPath(closestPin);
            if (audioPath && audioEnabled) {
              try {
                await window.audioContextForApp?.resume();
              } catch {}
              audioManager.playSimple(audioPath);
              setLastPlayedSpot(closestPin.id);
            }
          }
        }
        setLastNearSpot(closestPin.id);
      }
    } else {
      if (lastNearSpot) {
        setLastNearSpot(null);
        setLastPlayedSpot(null);
        if (proximityModal.show) {
          setProximityModal({ show: false, spotInfo: null });
        }
      }
    }
  }, [currentPosition, calculateDistance, dismissedSpots, lastNearSpot, getLocalizedText, lastPlayedSpot, getAudioPath, audioEnabled, proximityModal.show, proximityModal.spotInfo?.id, isPointInPolygon]);

  // 音声許可後の再生処理
  useEffect(() => {
    (async () => {
      const id = proximityModal.spotInfo?.id;
      if (!audioEnabled || !proximityModal.show || !id) return;
      if (lastPlayedSpot === id) return;
      
      const pin = pinLocations.find((p) => p.id === id);
      const audioPath = pin ? getAudioPath(pin) : null;
      if (!audioPath) return;
      
      try {
        await window.audioContextForApp?.resume();
      } catch {}
      audioManager.playSimple(audioPath);
      setLastPlayedSpot(id);
    })();
  }, [audioEnabled, proximityModal.show, proximityModal.spotInfo?.id, lastPlayedSpot, getAudioPath]);

  // 現在位置取得
  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('map.errors.geolocationNotSupported'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPosition = {
          lat: latitude,
          lng: longitude
        };
        setCurrentPosition(newPosition);
        setLocationError(null);
        
        if (isFollowingLocation) {
          setMapCenter(newPosition);
        }
      },
      (error) => {
        let errorMessage = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('map.errors.permissionDenied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('map.errors.positionUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('map.errors.timeout');
            break;
          default:
            errorMessage = t('map.errors.unknownError');
            break;
        }
        setLocationError(errorMessage);
      },
      options
    );
  }, [isFollowingLocation, t]);

  // 位置追従開始
  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('map.errors.geolocationNotSupported'));
      return null;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPosition = {
          lat: latitude,
          lng: longitude
        };
        setCurrentPosition(newPosition);
        setLocationError(null);
        
        if (isFollowingLocation) {
          setMapCenter(newPosition);
        }
      },
      (error) => {
        let errorMessage = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('map.errors.permissionDenied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('map.errors.positionUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('map.errors.timeout');
            break;
          default:
            errorMessage = t('map.errors.unknownError');
            break;
        }
        setLocationError(errorMessage);
      },
      options
    );

    return watchIdRef.current;
  }, [isFollowingLocation, t]);

  // ガイドセッション開始
  useEffect(() => {
    // まだ現在地が取れていない場合は何もしない
    if (!currentPosition) return;
  
    // すでにセッション開始処理を走らせている場合は何もしない
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true; // ★ ここで「もう実行済み」フラグを立てる
  
    const initializeSession = async () => {
      try {
        const newSessionId = await startGuideSession(currentPosition);
        setSessionId(newSessionId);
        console.log('Guide session started:', newSessionId);
      } catch (error) {
        console.error('Failed to start guide session:', error);
      }
    };
  
    initializeSession();
  }, [currentPosition]); // ★ sessionId 依存は不要

  // 位置情報の定期送信
  useEffect(() => {
    if (!sessionId || !currentPosition) return;

    const sendLocationPeriodically = () => {
      saveLocation(sessionId, {
        lat: currentPosition.lat,
        lng: currentPosition.lng,
        accuracy: currentPosition.accuracy || null
      });
    };

    sendLocationPeriodically();

    locationTimerRef.current = setInterval(
      sendLocationPeriodically, 
      LOCATION_UPLOAD_INTERVAL * 1000
    );

    return () => {
      if (locationTimerRef.current) {
        clearInterval(locationTimerRef.current);
      }
    };
  }, [sessionId, currentPosition]);

  // エフェクト
  useEffect(() => {
    getCurrentPosition();
  }, [getCurrentPosition]);

  useEffect(() => {
    if (currentPosition?.lat && currentPosition?.lng) {
      const tid = setTimeout(() => {
        checkProximity();
      }, 500);
      return () => clearTimeout(tid);
    }
  }, [currentPosition, checkProximity]);

  useEffect(() => {
    checkAreaBoundary();
  }, [checkAreaBoundary]);

  useEffect(() => {
    if (isFollowingLocation) {
      startLocationTracking();
    } else {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    }

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (locationTimerRef.current) {
        clearInterval(locationTimerRef.current);
      }
      audioManager.stop();
      if (outsideAreaAlertTimerRef.current) {
        clearInterval(outsideAreaAlertTimerRef.current);
        outsideAreaAlertTimerRef.current = null;
      }
    };
  }, [isFollowingLocation, startLocationTracking]);

  useEffect(() => {
    return () => {
      if (outsideAreaTimer) {
        clearTimeout(outsideAreaTimer);
      }
    };
  }, [outsideAreaTimer]);

  useEffect(() => {
    return () => {
      disableScreenAwake();
    };
  }, [disableScreenAwake]);

  // イベントハンドラー
  const handleGuideEnd = async () => {
    try {
      audioManager.stop();
      if (outsideAreaAlertTimerRef.current) {
        clearInterval(outsideAreaAlertTimerRef.current);
        outsideAreaAlertTimerRef.current = null;
      }
      await disableScreenAwake();
      
      if (sessionId) {
        await endGuideSession(sessionId, currentPosition);
        console.log('Guide session ended');
      }
      
      if (locationTimerRef.current) {
        clearInterval(locationTimerRef.current);
        locationTimerRef.current = null;
      }
      
      navigate('/hattoriryokuchi/returntutorial');
    } catch (error) {
      console.error('Failed to end guide session:', error);
      navigate('/hattoriryokuchi/returntutorial');
    }
  };

  const handleSpotList = () => {
    setShowSpotList(true);
  };

  const handleModeSwitch = () => {
    const newFollowingMode = !isFollowingLocation;
    setIsFollowingLocation(newFollowingMode);
    
    if (newFollowingMode && currentPosition) {
      setMapCenter(currentPosition);
      alert(t('map.alerts.trackingModeOn'));
    } else {
      alert(t('map.alerts.trackingModeOff'));
    }
  };

  const closeSpotList = () => {
    setShowSpotList(false);
  };

  const closeStampList = () => {
    setShowStampList(false);
  };

  const closeActiveModal = () => {
    setActiveModal({ type: null, data: null });
    setTargetZoom(null);
  };

  // SpotListModalからのスポットクリック処理
  const handleSpotClick = (spot) => {
    const spotPin = pinLocations.find(pin => 
      getLocalizedText(pin.title) === spot.name
    );
    
    if (spotPin) {
      const spotPosition = { lat: spotPin.latitude, lng: spotPin.longitude };
      
      setIsSpotMoving(true);
      setMapCenter(spotPosition);
      setTargetZoom(18);
      
      if (isFollowingLocation) {
        setIsFollowingLocation(false);
      }
      
      setTimeout(() => {
        setIsSpotMoving(false);
        setTargetZoom(null);
      }, 1500);

      const modalType = categoryModalMap[spotPin.category] || 'info';
      
      // クイズデータの構造を整形
      let modalData = {
        id: spotPin.id,
        name: getLocalizedText(spotPin.title),
        image: spotPin.thumbnail,
        description: getLocalizedText(spotPin.description),
        audioPath: getAudioPath(spotPin),
        video: getVideoData(spotPin),
        category: spotPin.category,
        completed: completedStampIds.has(spotPin.id),
        isNearby: isWithinProximity(spotPin), // ★ 追加
        onRouteSearch: () => handleRouteSearch(spotPin),
        routeLoading,
      };
      
      // Select_Quizの場合、データ構造を調整
      if (spotPin.category === 'Select_Quiz' && spotPin.data) {
        modalData = {
          ...modalData,
          title: spotPin.data.title,
          grid: spotPin.data.grid,
          summary: spotPin.summary,
          question: spotPin.data.question || spotPin.summary,
          answers: spotPin.data.choices,
          answer: spotPin.data.answer
        };
      }
      // Input_Quizの場合
      else if (spotPin.category === 'Input_Quiz' && spotPin.data) {
        modalData = {
          ...modalData,
          ...spotPin.data
        };
      }
      // その他のカテゴリ
      else if (spotPin.data) {
        modalData = {
          ...modalData,
          ...spotPin.data
        };
      }
      
      setActiveModal({
        type: modalType,
        data: modalData
      });
    }
  };

  const closeQuizResult = () => {
    setQuizResult({ show: false, isCorrect: false });
  };

  // ProximityModal閉じる処理
  const closeProximityModal = useCallback(() => {
    const id = proximityModal.spotInfo?.id;
    if (id !== null && id !== undefined) {
      setDismissedSpots((prev) => new Set(prev).add(id));
    }
    setLastPlayedSpot(null);
    setProximityModal({ show: false, spotInfo: null });
  }, [proximityModal.spotInfo?.id]);

  // ProximityModalクリック処理
  const handleProximityModalClick = (spotId) => {
    const pin = pinLocations.find(p => p.id === spotId);
    if (pin) {
      setProximityModal({ show: false, spotInfo: null });
      
      const modalType = categoryModalMap[pin.category] || 'info';
      
      // クイズデータの構造を整形
      let modalData = {
        id: pin.id,
        name: getLocalizedText(pin.title),
        image: pin.thumbnail,
        description: getLocalizedText(pin.description),
        audioPath: getAudioPath(pin),
        video: getVideoData(pin),
        category: pin.category,
        completed: completedStampIds.has(pin.id),
        isNearby: isWithinProximity(pin), // ★ 追加
        onRouteSearch: () => handleRouteSearch(pin),
        routeLoading,
      };
      
      // Select_Quizの場合、データ構造を調整
      if (pin.category === 'Select_Quiz' && pin.data) {
        modalData = {
          ...modalData,
          title: pin.data.title,
          grid: pin.data.grid,
          summary: pin.summary,
          question: pin.data.question || pin.summary,
          answers: pin.data.choices,
          answer: pin.data.answer
        };
      }
      // Input_Quizの場合
      else if (pin.category === 'Input_Quiz' && pin.data) {
        modalData = {
          ...modalData,
          ...pin.data
        };
      }
      // その他のカテゴリ
      else if (pin.data) {
        modalData = {
          ...modalData,
          ...pin.data
        };
      }
      
      setActiveModal({
        type: modalType,
        data: modalData
      });
    }
  };

  // ピン直接クリック処理
  const handlePinClick = (pin) => {
    if (isSpotMoving) return;
    
    setTargetZoom(null);
    setIsSpotMoving(false);
    
    const modalType = categoryModalMap[pin.category] || 'info';
    
    // クイズデータの構造を整形
    let modalData = {
      id: pin.id,
      name: getLocalizedText(pin.title),
      image: pin.thumbnail,
      description: getLocalizedText(pin.description),
      audioPath: getAudioPath(pin),
      video: getVideoData(pin),
      category: pin.category,
      completed: completedStampIds.has(pin.id),
      isNearby: isWithinProximity(pin), // ★ 追加
      onRouteSearch: () => handleRouteSearch(pin),
      routeLoading,
    };
    
    // Select_Quizの場合、データ構造を調整
    if (pin.category === 'Select_Quiz' && pin.data) {
      modalData = {
        ...modalData,
        title: pin.data.title,
        grid: pin.data.grid,
        summary: pin.summary,
        question: pin.data.question || pin.summary,
        answers: pin.data.choices,
        answer: pin.data.answer
      };
    }
    // Input_Quizの場合
    else if (pin.category === 'Input_Quiz' && pin.data) {
      modalData = {
        ...modalData,
        ...pin.data
      };
    }
    // その他のカテゴリ
    else if (pin.data) {
      modalData = {
        ...modalData,
        ...pin.data
      };
    }
    
    setActiveModal({
      type: modalType,
      data: modalData
    });
  };

  const returnToMap = () => {
    // カウント更新は handleStampGet に集約済み
    closeQuizResult();
    closeActiveModal();
  };

  // 正解結果の表示。正解時はスタンプ獲得＆再挑戦不可化
  const showQuizResult = (isCorrect) => {
    setQuizResult({ show: true, isCorrect });
    if (isCorrect && (activeModal.type === 'quiz' || activeModal.type === 'inputQuiz')) {
      const spotId = activeModal.data?.id;
      if (spotId) {
        handleStampGet(spotId);
        // モーダル内 completed 状態を即時反映
        setActiveModal(prev => ({
          ...prev,
          data: prev?.data ? { ...prev.data, completed: true } : prev.data
        }));
      }
    }
  };

  const handleCurrentPositionClick = () => {
    if (currentPosition) {
      alert(`${t('map.currentLocation')}\n緯度: ${currentPosition.lat.toFixed(6)}\n経度: ${currentPosition.lng.toFixed(6)}`);
    } else {
      alert(t('map.alerts.gettingLocation', '現在地を取得中です'));
    }
  };

  return (
    <div className="map-wrapper">
      <div className="map-container">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={zoom}
          style={mapContainerStyle}
          className="leaflet-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapController 
            center={mapCenter} 
            shouldPanTo={isFollowingLocation || isSpotMoving} 
            targetZoom={targetZoom}
            resetZoom={false}
          />

          {routePath.length > 0 && (
            <Polyline
              positions={routePath}
              pathOptions={routeStyle}
            />
          )}
          
          {/* スポットマーカー */}
          {pinLocations.map((pin, index) => {
            const isCompleted = completedStampIds.has(pin.id);
            return (
              <Marker
                key={pin.id || index}
                position={[pin.latitude, pin.longitude]}
                icon={customPinIcon(pin.category, isCompleted)}
                eventHandlers={{
                  click: (e) => {
                    e.target.closePopup();
                    handlePinClick(pin);
                  }
                }}
              >
                {!activeModal.type && (
                  <Popup>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0' }}>{getLocalizedText(pin.title)}</h3>
                      <p style={{ margin: 0, fontSize: '12px' }}>
                        {getLocalizedText(pin.summary || pin.description)}
                      </p>
                    </div>
                  </Popup>
                )}
              </Marker>
            );
          })}
          
          {/* 現在地マーカー */}
          <CurrentLocationMarker 
            position={currentPosition} 
            onClick={handleCurrentPositionClick}
            t={t}
          />

          {/* 移動可能エリアのポリゴン */}
          {showArea && movableAreas.map((area, index) => (
            <Polygon
              key={`movable-area-${index}`}
              positions={area}
              pathOptions={polygonStyle}
            />
          ))}
        </MapContainer>
      </div>
      
      <button className="guide-end-button" onClick={handleGuideEnd}>
        {t('map.buttons.endGuide')}
      </button>
      
      {locationError && (
        <div className="location-error">
          <span>{locationError}</span>
          <button
            onClick={() => navigate('/hattoriryokuchi/locationsetting')}
            className="retry-button"
            aria-label={t('map.buttons.retry')}
          >
            {t('map.buttons.retry')}
          </button>
        </div>
      )}

      {isOutsideArea && showArea && (
        <div className="area-warning">
          <span>⚠️ {t('map.alerts.outsideArea')}</span>
        </div>
      )}
      
      <div className="bottom-buttons-container">
        <button className="bottom-button" onClick={handleSpotList}>
          {t('map.buttons.spotList')}
        </button>
        <button className="bottom-button" onClick={handleModeSwitch}>
          {t('map.buttons.modeSwitch')}
        </button>
      </div>

      <SpotListModal 
        isOpen={showSpotList}
        onClose={closeSpotList}
        spots={spotData}
        onSpotClick={handleSpotClick}
      />

      <StampListModal 
        isOpen={showStampList}
        onClose={closeStampList}
        stamps={stampsWithStatus}
        onStampClick={handleSpotClick}
      />

      <QuizModal
        isOpen={activeModal.type === 'quiz'}
        onClose={closeActiveModal}
        quiz={{
          ...activeModal.data,
          routeLoading,
        }}
        onShowResult={showQuizResult}
      />

      <InfoModal
        isOpen={activeModal.type === 'info'}
        onClose={closeActiveModal}
        info={{
          ...activeModal.data,
          routeLoading,
        }}
      />

      <VideoModal
        isOpen={activeModal.type === 'video'}
        onClose={closeActiveModal}
        video={{
          ...activeModal.data?.video,
          name: activeModal.data?.name,
          description: activeModal.data?.description,
          onRouteSearch: activeModal.data?.onRouteSearch,
          routeLoading,
        }}
      />

      <StampModal
        isOpen={activeModal.type === 'stamp'}
        onClose={closeActiveModal}
        stamp={{
          ...activeModal.data,
          routeLoading,
        }}
        onStampGet={handleStampGet}
      />

      <InputQuizModal
        isOpen={activeModal.type === 'inputQuiz'}
        onClose={closeActiveModal}
        quiz={{
          ...activeModal.data,
          routeLoading,
        }}
        onShowResult={showQuizResult}
      />

      <QuizResultModal
        isOpen={quizResult.show}
        onClose={closeQuizResult}
        onReturnToMap={returnToMap}
        isCorrect={quizResult.isCorrect}
      />

      <ProximityModal
        isOpen={proximityModal.show}
        onClose={closeProximityModal}
        spotInfo={proximityModal.spotInfo}
        onModalClick={handleProximityModalClick}
      />

      <AudioPermissionModal
        isOpen={showAudioPermission}
        onAllow={handleAudioAllow}
        onDeny={handleAudioDeny}
      />

      <CongratulationsModal
        isOpen={showCongratulations}
        onClose={closeCongratulations}
      />
    </div>
  );
}

export default Map;